import { createApp } from 'vue'
import Cookies from 'js-cookie'
import axios from 'axios'
import pinia from "@/stores/create-store"
import router from '@/router'
import App from '@/App.vue'
import vSelect from 'vue-select'
import VueTippy from 'vue-tippy'
// Directives
import { autosizeTextareas, autosizeTextareasInput } from '@/directives/forms'
import { convertNewlinesDirective, renderDirective } from  '@/directives/rendering'
// Stores
import { useAuthStore } from '@/stores/user/auth'
import { useUserStore } from '@/stores/user/user'
import { useUserSettingsStore } from '@/stores/user/settings'
import { useDashboardStore } from '@/stores/user/dashboard'
import { useUserTagsStore } from '@/stores/user/tags'
import { useViewLoadingStore } from '@/stores/loading/view-loading'
import { usePopoverStore } from '@/stores/interactions/popovers'
import { useNavigationStore } from '@/stores/interactions/navigation'
import { useCollectionsStore } from '@/stores/collections/collections'
import { useGinnelsStore } from '@/stores/ginnels/ginnels'
import { useGinnelStore } from '@/stores/ginnels/ginnel'
import { useToastsStore } from '@/stores/notifications/toasts'
import { useFilesStore } from '@/stores/ginnels/files'
import { useSearchStore } from '@/stores/interactions/search'
import { useAppStore } from '@/stores/app/app'
import { useRefStore } from '@/stores/app/references'
import { useGinnelTagsStore } from '@/stores/ginnels/tags'

// Development Components
import TestForm from '@/components/Development/TestForm.vue'
import TestFormWrapper from '@/components/Development/TestComponents/TestFormWrapper.vue'
import Dump from '@/components/Development/Dump.vue'

// Components
import Modal from '@/components/Bootstrap/Modals/Modal.vue'
import Loader from '@/components/Loading/Loader.vue'
import LoadingBar from '@/components/Loading/LoadingBar.vue'
import CodeGinnelLogo from '@/components/Branding/CodeGinnelLogo.vue'
import PrimaryButton from '@/components/Buttons/PrimaryButton.vue'
import SecondaryButton from '@/components/Buttons/SecondaryButton.vue'
import DangerButton from '@/components/Buttons/DangerButton.vue'
import ProfilePhoto from '@/components/User/ProfilePhoto.vue'
import Image from '@/components/Media/Image.vue'
import HelpIcon from '@/components/Icons/HelpIcon.vue'
// Config
import config from '@/config'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

import { library } from '@fortawesome/fontawesome-svg-core'
import 'bootstrap'
import 'vue-select/dist/vue-select.css'
import '@/assets/sass/app.scss'
import 'tippy.js/dist/tippy.css'

import { fas } from '@fortawesome/free-solid-svg-icons'
import { fab } from '@fortawesome/free-brands-svg-icons'
import { far } from '@fortawesome/free-regular-svg-icons'

library.add(fas) // Solid
library.add(fab) // Brands
library.add(far) // Regular

axios.defaults.baseURL = process.env.API_BASE_URL
axios.defaults.withCredentials = true
axios.interceptors.request.use(function (config) {
   config.headers['X-XSRF-TOKEN'] = Cookies.get('XSRF-TOKEN')

   return config;
});

const app = createApp(App)
const storeApp = useAppStore(pinia)
app.config.globalProperties.$environment = storeApp.environment
app.config.globalProperties.$localMedia = config.localMedia

app
.component('TestForm', TestForm)
.component('TestFormWrapper', TestFormWrapper)
.component('Dump', Dump)

.component('Modal', Modal)
.component('Loader', Loader)
.component('LoadingBar', LoadingBar)
.component('FontAwesomeIcon', FontAwesomeIcon)
.component('Logo', CodeGinnelLogo)
.component('PrimaryButton', PrimaryButton)
.component('SecondaryButton', SecondaryButton)
.component('DangerButton', DangerButton)
.component('ProfilePhoto', ProfilePhoto)
.component('v-select', vSelect)
.component('Image', Image)
.component('HelpTooltip', HelpIcon)

app.provide('$storeAuth', useAuthStore(pinia))
app.provide('$storeUser', useUserStore(pinia))
app.provide('$storeUserSettings', useUserSettingsStore(pinia))
app.provide('$storeViewLoading', useViewLoadingStore(pinia))
app.provide('$storePopovers', usePopoverStore(pinia))
app.provide('$storeNavigation', useNavigationStore(pinia))
app.provide('$storeCollections', useCollectionsStore(pinia))
app.provide('$storeGinnels', useGinnelsStore(pinia))
app.provide('$storeGinnel', useGinnelStore(pinia))
app.provide('$storeToasts', useToastsStore(pinia))
app.provide('$storeFiles', useFilesStore(pinia))
app.provide('$storeSearch', useSearchStore(pinia))
app.provide('$storeDashboard', useDashboardStore(pinia))
app.provide('$storeApp', storeApp)
app.provide('$storeRefs', useRefStore(pinia))
app.provide('$storeUserTags', useUserTagsStore(pinia))
app.provide('$storeGinnelsTags', useGinnelTagsStore(pinia))

app.directive('render', convertNewlinesDirective)
app.directive('autosize', autosizeTextareas)
app.directive('autosize-single', autosizeTextareasInput)

app.use(
   VueTippy,
   {
     directive: 'tippy', // => v-tippy
     component: 'tippy', // => <tippy/>

     defaultProps: {
       content: reference => reference.getAttribute('title'),
       duration: [0, 400],
       touch: false
     },
     allowHTML: true,
   }
 )
app.use(pinia)
app.use(router)
app.mount('#app')
